import { getFieldData } from "../../../utils/FormFields/FieldsSetting/SetFieldSetting";

/**
 * Function to map options to a specific field in the form configuration.
 * 
 * @param {Object} formConfig - The entire form configuration object.
 * @param {string} sectionId - The ID of the section containing the field.
 * @param {string} fieldId - The ID of the field to map options to.
 * @param {Array} data - The array of data to be used for generating options.
 * @param {string} valueKey - The key to be used for the option's value.
 * @param {string} labelKey - The key to be used for the option's label.
 */
export const mapDropdownOptions = (formConfig, sectionId, fieldId, data, valueKey, labelKey, extraKeys) => {
    // Find the specified section
    const section = formConfig.section.find((section) => section.sectionId === sectionId);

    if (!section) return;

    let dropdownField;

    // Check if the section has fields at the top level
    if (section.fields) {
        dropdownField = section.fields.find((field) => field.id === fieldId);
    }

    // If not found in top-level fields, check within rowGroups
    if (!dropdownField && section.rowGroup) {
        section.rowGroup.some((row) => {
            dropdownField = row.fields.find((field) => field.id === fieldId);
            return dropdownField; // Break out of the loop if field is found
        });
    }

    // If the field exists, map the data to the dropdown options
    if (dropdownField) {
        dropdownField.fieldSetting.options = data.map((item) => {

            const options = {
                value: item[valueKey],
                label: item[labelKey],
            }

            // Dynamically add additional properties from extraKeys
            extraKeys?.forEach(key => {
                if (item[key]) {
                    options[key] = item[key]
                }
            });

            return options;
        });
    }
};


/**
 * Maps dropdown options to a specific field in the form configuration.
 * 
 * @param {Object} formConfig - The entire form configuration object.
 * @param {string} sectionId - The ID of the section containing the field.
 * @param {string} fieldId - The ID of the field to map options to.
 * @param {Array} data - The array of data to be used for generating options.
 * @param {string} valueKey - The key to be used for the option's value.
 * @param {string} labelKey - The key to be used for the option's label.
 * @param {Array} extraKeys - Additional keys to include in each option object.
 * @returns {Object} The same formConfig object with updated dropdown options.
 */
export const mapDropdownOptions2 = (formConfig, sectionId, fieldId, data, valueKey, labelKey, extraKeys = []) => {
    const section = formConfig.section.find((section) => section.sectionId === sectionId);
    if (!section) return formConfig;

    let dropdownField;

    // Check top-level fields
    if (section.fields) {
        dropdownField = section.fields.find((field) => field.id === fieldId);
    }

    // Check rowGroups if not found in top-level fields
    if (!dropdownField && section.rowGroup) {
        section.rowGroup.some((row) => {
            dropdownField = row.fields.find((field) => field.id === fieldId);
            return dropdownField;
        });
    }

    // Map options if the field is found
    if (dropdownField) {
        dropdownField.fieldSetting.options = data.map((item) => {
            const option = {
                value: item[valueKey],
                label: item[labelKey],
            };
            extraKeys.forEach((key) => {
                if (item[key] !== undefined) {
                    option[key] = item[key];
                }
            });
            return option;
        });
    }

    return formConfig;
};


export const setDropdownOptions = (dropdownOptionsData, valueField, labelField, formConfig, fieldId, setFormData, filterCondition = null, extraKeys = []) => {

    // Filter the API response data if a filter condition is provided
    const filteredData = filterCondition ? dropdownOptionsData?.filter(filterCondition) : dropdownOptionsData;

    // Map the filtered data to the required format
    const mappedData = filteredData?.map((item) => {
        const options = {
            value: item[valueField],
            label: item[labelField],
        }

        // Dynamically add additional properties from extraKeys
        extraKeys?.forEach(key => {
            if (key in item) {
                options[key] = item[key];
            }
        });

        return options;
    });

    // Find the dropdown field within the form fields data
    const dropdownField = getFieldData(formConfig, fieldId);

    // If the dropdown field is found, set its options to the mapped data
    if (dropdownField) {
        dropdownField.fieldSetting.options = mappedData;
    }
    setFormData && setFormData({ ...formConfig });
};